import { ProductoService } from '@abm/producto/producto.service';
import { Component, EventEmitter, OnDestroy, OnInit, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ClienteService } from '@cliente/cliente.service';
import { NgbCalendar, NgbDateStruct, NgbDatepickerConfig } from '@ng-bootstrap/ng-bootstrap';
import { TipoCuenta } from '@shared/enums/tipoCuenta';
import { TipoProducto } from '@shared/enums/tipoProducto';
import { Cliente } from '@shared/models/despacho/Cliente';
import { CuentaCliente } from '@shared/models/despacho/CuentaCliente';
import { Producto } from '@shared/models/despacho/Producto';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';


export interface AerovaleFacturaAnticipadaValues {
  clienteFormControl: Cliente;
  productoFormControl: Producto;
  volumenFormControl: string;
  fechaHastaFormControl: NgbDateStruct;
}

@Component({
  selector: 'app-aerovale-factura-anticipada',
  templateUrl: './aerovale-factura-anticipada.component.html',
  styleUrl: './aerovale-factura-anticipada.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AerovaleFacturaAnticipadaComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AerovaleFacturaAnticipadaComponent),
      multi: true
    }
  ]
})
export class AerovaleFacturaAnticipadaComponent implements ControlValueAccessor, OnDestroy, OnInit {

  @Output() formularioValido = new EventEmitter();
  @Output() objectAnticipadaForm = new EventEmitter();

  form: UntypedFormGroup;
  subscriptions: Subscription[] = [];
  cargandoClientes: boolean;
  obteniendoProductos: boolean;
  esAerovaleDevolucion: boolean;
  combustibles: Producto[];
  clientes: CuentaCliente[];
  errorClientes: boolean;
  clienteSeleccionado: boolean;
  clienteSeleccionadoLista: CuentaCliente;
  fechaHoy: NgbDateStruct;
  fechaHasta: NgbDateStruct;

  constructor(private formBuilder: UntypedFormBuilder, private productosService: ProductoService,
    private clienteService: ClienteService, private calendar: NgbCalendar, private datepickerConfig2: NgbDatepickerConfig) { }

  ngOnInit() {
    this.fechaHoy = this.calendar.getToday();
    this.crearFormulario();
    this.inicializarCalendario();
    this.inicializarComponente();
    this.getCombustibles();
    this.getClientes();
  }


  crearFormulario() {
    this.form = this.formBuilder.group(
      {
        clienteFormControl: ['', Validators.required],
        productoFormControl: ['', Validators.required],
        volumenFormControl: ['', Validators.required],
        fechaHastaFormControl: ['', Validators.required]
      }
    );
    this.objectAnticipadaForm.emit(this.form);
  }

  inicializarComponente() {
    this.subscriptions.push(
      this.form.valueChanges.subscribe(value => {
        this.onChange(value);
        this.onTouched();
      })
    );

    this.form.valueChanges.subscribe(() => {
      this.formularioValido.emit(this.form.valid);
    });
  }

  inicializarCalendario() {
    this.fechaHasta = this.fechaHoy;
    this.datepickerConfig2.minDate = this.fechaHoy;
    this.datepickerConfig2.maxDate = { ...this.fechaHoy, year: this.fechaHoy.year + 1 }
  }

  getCombustibles(): void {
    this.obteniendoProductos = true;
    this.productosService.obtenerProductos()
      .subscribe(combustibles => {
        this.combustibles = combustibles.filter(c => c.tipoProducto == TipoProducto.Aerocombustible);
        this.obteniendoProductos = false;
      });
  }

  getClientes(): void {
    this.cargandoClientes = true;
    this.clienteService.getClientesActivos()
      .subscribe(clientes => {
        this.clientes = clientes.map(a => Object.assign(new CuentaCliente(), a));
        this.clientes = this.clientes.filter(c => c.tipoCuenta == TipoCuenta.Anticipada);
        this.cargandoClientes = false;
      }, () => this.errorClientes = true);
  }

  confirmCustomer(): void {
    if (typeof (this.clienteFormControl.value) === 'string') {
      this.clienteFormControl.setValue(null);
    }
  }

  get value(): AerovaleFacturaAnticipadaValues {
    return this.form.value;
  }

  set value(value: AerovaleFacturaAnticipadaValues) {
    this.form.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  get productoFormControl() {
    return this.form.controls.productoFormControl;
  }

  get volumenFormControl() {
    return this.form.controls.volumenFormControl;
  }

  get fechaHastaFormControl() {
    return this.form.controls.fechaHastaFormControl;
  }

  get clienteFormControl() {
    return this.form.controls.clienteFormControl;
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onChange: any = () => { };// eslint-disable-line
  onTouched: any = () => { };// eslint-disable-line

  registerOnChange(fn) {
    this.onChange = fn;
  }

  writeValue(value) {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.form.reset();
    }
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  validate(_: UntypedFormControl) {// eslint-disable-line
    return this.form.valid ? null : { aerovaleCombustible: { valid: false } };
  }

  formatter = (x: { razonSocial: string, ctA_SGC: string }) => `${x.razonSocial} - ${x.ctA_SGC == null ? 'SIN CTA SGC' : x.ctA_SGC}`;

  search = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => this.clientes == null || term.length < 2 || this.cargandoClientes ? [] :
        this.clientes.filter(v => this.containsString(this.clienteFormControl.value, v.ctA_SGC, v.razonSocial)).slice(0, 10))
    )


  selectCustomer(e: any): void {
    this.clienteFormControl.setValue(e.item);
    this.clienteSeleccionadoLista = e.item;
  }

  containsString(searchValue: string, ...searchIn: string[]): boolean {
    const concat = searchIn.toString();
    return concat.toLowerCase().indexOf(searchValue.toLowerCase()) > -1
  }

  esCampoVacio(campo: any): boolean {
    return (campo == null || (campo != null && campo.length == 0));
  }

}
